/**
 * The class strings every admin region shares.
 *
 * They live in one module rather than beside each primitive so that a change to
 * the admin panel's rhythm is one edit. Nothing outside
 * `features/admin/components/layout` should import these: a page that reaches
 * for a layout token is a page laying itself out, which is what the region
 * contract exists to prevent. See `README.md` in this folder.
 */

/** Section and metric labels: the one uppercase label style in the panel. */
export const ADMIN_LABEL_CLASS = "text-[11px] uppercase tracking-wider text-muted-foreground";

/** The bordered card every panel is drawn on. */
export const ADMIN_PANEL_CLASS =
  "flex min-h-0 shrink-0 flex-col overflow-hidden rounded-md border border-border bg-card";

/** A panel's header band, which is also where its own controls mount. */
export const ADMIN_PANEL_HEADER_CLASS =
  "flex flex-wrap items-center gap-2 border-b border-border px-3 py-2";

/**
 * Column headers, matching `@nexus/ui`'s `TableHead` exactly — `font-normal`
 * and `tracking-wide`, not the `font-medium tracking-wider` the hand-rolled
 * admin tables drifted to. DESIGN.md owns this; the admin panel follows it.
 */
export const ADMIN_TABLE_HEADER_CLASS =
  "whitespace-nowrap border-b border-border px-3 py-2 text-left text-[11px] font-normal uppercase tracking-wide text-muted-foreground";

export const ADMIN_CELL_CLASS = "px-3 py-2 text-xs text-foreground";

/**
 * Every number that can change between renders is tabular, and every numeric
 * column is right-aligned, so digits line up down the column and across rows.
 * `whitespace-nowrap` keeps a squeezed column from breaking a value between its
 * number and its unit.
 */
export const ADMIN_NUMERIC_CELL_CLASS = "text-right tabular-nums whitespace-nowrap";

/**
 * Keeps column headers legible over rows sliding under them.
 * Stay at z-[1]: chart tooltips use z-50 so they can overflow the plot into
 * the table without the sticky head covering them.
 */
export const ADMIN_STICKY_HEAD_CLASS = "sticky top-0 z-[1] bg-card";

/** Height of one table row, used to cap a panel on a row boundary. */
export const ADMIN_TABLE_ROW_HEIGHT_PX = 33;

/** What a value renders as when it is unknown. Never a bare zero. */
export const ADMIN_DASH = "—";
